// Which characters belong to the campaign that is open, and the combat state
// that belongs with them.
//
// The layout keeps ONE flat `characters` list (every sheet the console has
// ever read) and a snapshot per campaign that names its roster by source id.
// Everything that shows "the party" therefore has to filter, or the other
// table's characters sit in the tracker — which is exactly what happened at
// the table on 2026-09-23: switching campaigns changed the name in the
// picker and nothing else.
//
// The fallback matters: a campaign whose snapshot has no roster ids yet (one
// made by hand, or synced before the importer recorded them) shows everyone,
// because showing nobody would look like the party had vanished.
import type { Character, Layout } from "./types";
import { normLoose } from "./text";

/* rosterSourceIds holds D&D Beyond SOURCE ids - bare numbers - which become
   character ids by gaining a "ddb-" prefix. It also ends up holding whole
   character ids, because claimForCampaign stores whatever it is handed. Only a
   bare number is a source id; anything already carrying a kind is an id.

   Prefixing everything is what stopped a custom character ever being claimed.
   "custom-mg1x7k" was stored as "ddb-custom-mg1x7k", which matches no character
   on earth, so the claim reported success and the character never appeared. */
const BARE_SOURCE_ID = /^[0-9]+$/;
const MANGLED = /^ddb-((?:custom|local|npc)-.+)$/;
const withPrefix = (raw: string) => {
  const id = String(raw);
  if (BARE_SOURCE_ID.test(id)) return `ddb-${id}`;
  // Repair what the old rule stored, so a campaign claimed before this fix
  // finds its characters instead of needing them claimed again.
  const mangled = MANGLED.exec(id);
  return mangled ? mangled[1] : id;
};

/** The source ids the active campaign claims, "ddb-" prefixed. Empty = claims nothing yet. */
export function rosterIdsOf(layout: Layout, campaign = layout.activeCampaign): Set<string> {
  const snap = layout.campaignSnapshots.find((c) => c.name === campaign);
  const ids = (snap?.rosterSourceIds || []).map(withPrefix);
  // characters recorded in the snapshot count too (an old export kept them there)
  for (const c of snap?.characters || []) if (c?.id) ids.push(c.id);
  return new Set(ids);
}

/** The party of the campaign that is open — exactly what it claims, including nothing. */
export function campaignRoster(layout: Layout, campaign = layout.activeCampaign): Character[] {
  const ids = rosterIdsOf(layout, campaign);
  // This used to fall back to EVERY character the console had ever read, and it
  // did so twice: once when the campaign claimed no ids at all, and again when it
  // claimed ids none of which were loaded. The intent was kind — an empty party
  // looks like the party vanished — but at the table on 2026-09-25 it put the
  // whole Vaelis roster on screen after a switch to Graycrest, and the DM spent
  // game time deleting PCs that belong to another table by hand.
  //
  // Showing the wrong party is a worse lie than showing none: it reads as a
  // broken sync, and it says nothing at all. So the roster is now the claim, and
  // the empty case is explained where it shows — PartyOverview says the campaign
  // claims nobody and offers to claim the loaded sheets, which is what the
  // fallback was really standing in for.
  return layout.characters.filter((c) => ids.has(c.id));
}

/** Add characters to the open campaign's roster (the importer calls this, so the
 *  next switch away from this campaign actually leaves them behind). */
export function claimForCampaign(layout: Layout, ids: string[]): Layout {
  if (!ids.length) return layout;
  const name = layout.activeCampaign;
  if (!name) return layout;
  const known = layout.campaignSnapshots.some((s) => s.name === name);
  const claim = (snap: Layout["campaignSnapshots"][number]) => {
    const have = new Set((snap.rosterSourceIds || []).map(withPrefix));
    const add = ids.map(withPrefix).filter((id) => !have.has(id));
    return add.length ? { ...snap, rosterSourceIds: [...(snap.rosterSourceIds || []), ...add] } : snap;
  };
  const snapshots = known
    ? layout.campaignSnapshots.map((s) => (s.name === name ? claim(s) : s))
    : [...layout.campaignSnapshots, claim({ name, sourceId: "", sourceUrl: "", rosterSourceIds: [], lastSyncedAt: new Date().toISOString(), characters: [] })];
  return { ...layout, campaignSnapshots: snapshots };
}

/** The open campaign's characters that are NOT open on D&D Beyond right now.
 *
 *  `openSourceIds` are the bare D&D Beyond ids of the sheets open in the browser
 *  (from the connector's open-tab list). A roster character counts as open only
 *  when its id is `ddb-<one of those>`; a custom character (no sheet) can never
 *  match, so it is always "not open" — the caller warns about that before it
 *  removes anything. Pure: it decides WHO, the layout write is unclaimFromCampaign. */
export function notOpenOnDdb(roster: Character[], openSourceIds: string[]): Character[] {
  const open = new Set(openSourceIds.map((id) => `ddb-${String(id).replace(/^ddb-/, "")}`));
  return roster.filter((c) => !open.has(c.id));
}

/** Who a prune-on-sync would remove — the whole destructive decision in one pure
 *  place, so it can be tested rather than assembled inline in the component.
 *
 *  A character is a candidate only when we have an authoritative "open" answer
 *  (openTargets is the connector's open-tab list; null means it has not replied
 *  yet, so NOTHING is prunable). The keep set is everyone open, everyone just
 *  imported, AND everyone whose read FAILED — a failed read is "unknown", not
 *  "not open", and must never cost a character (the audit's data-loss path). */
export function prunableNotOpen(
  roster: Character[],
  openTargets: { sourceId: string }[] | null,
  importedSourceIds: string[],
  failedSourceIds: string[],
): Character[] {
  if (openTargets === null) return [];
  const keep = [...openTargets.map((t) => t.sourceId), ...importedSourceIds, ...failedSourceIds];
  return notOpenOnDdb(roster, keep);
}

/** Remove characters from a campaign's roster: drop their ids from the snapshot's
 *  rosterSourceIds (whatever form they were stored in) and from snap.characters.
 *  The Character objects stay in the flat list — another campaign may claim them,
 *  and a later sync re-claims an open one — so this un-claims from THIS campaign
 *  rather than deleting globally. Nothing happens for an empty id list. */
export function unclaimFromCampaign(layout: Layout, ids: string[], campaign = layout.activeCampaign): Layout {
  if (!ids.length) return layout;
  const remove = new Set(ids);
  const campaignSnapshots = layout.campaignSnapshots.map((snap) => {
    if (snap.name !== campaign) return snap;
    const rosterSourceIds = (snap.rosterSourceIds || []).filter((raw) => !remove.has(withPrefix(raw)));
    const characters = (snap.characters || []).filter((c) => !remove.has(c.id));
    return { ...snap, rosterSourceIds, characters };
  });
  return { ...layout, campaignSnapshots };
}

// ---- combat state, per campaign ----
// The tracker's own keys are global in the layout (they were written when the
// console only ever ran one table). Switching campaigns parks the current
// set under the campaign it belongs to and unpacks the other one's, so a
// fight left half-run in one game is still there when you come back.
export const COMBAT_KEYS = [
  "externalInitiative", "initiativeScores", "initiativeRollModes", "initiativeBonuses",
  "activeTurnId", "roundNumber", "surpriseRound", "turnsTaken", "initiativeAllies", "initiativeAliases",
  "initiativeHidden", "npcConditions", "npcLights", "visionProfiles", "roll20Tokens",
] as const;

const EMPTY_COMBAT: Record<string, unknown> = { externalInitiative: [], initiativeScores: {}, initiativeRollModes: {}, initiativeBonuses: {}, activeTurnId: "", roundNumber: 1, surpriseRound: false, turnsTaken: [], initiativeAllies: {}, initiativeAliases: {}, initiativeHidden: {}, npcConditions: {}, npcLights: {}, visionProfiles: {}, roll20Tokens: {} };

/** Park `from`'s combat state and unpack `to`'s. Nothing is thrown away. */
export function swapCombat(layout: Layout, from: string, to: string): Layout {
  const parked = { ...((layout.combatByCampaign as Record<string, Record<string, unknown>>) || {}) };
  if (from) parked[from] = Object.fromEntries(COMBAT_KEYS.map((k) => [k, layout[k]]));
  const next = parked[to] || EMPTY_COMBAT;
  return { ...layout, ...EMPTY_COMBAT, ...next, combatByCampaign: parked };
}

/** The open campaign's characters that have a D&D Beyond sheet behind them.
 *
 *  Almost every outward-facing feature wants exactly this set: the sheets this
 *  table may read, write, poll or push to a map. Spelling it out per component
 *  is how the console ended up with four different answers to "who is here" -
 *  the inventory listed every sheet ever read, the encounter builder rated
 *  difficulty against all of them, and Shell's bar sync carried a comment saying
 *  "the active campaign's characters" above a filter that had no campaign in it.
 */
export function campaignSheets(layout: Layout, campaign = layout.activeCampaign): Character[] {
  return campaignRoster(layout, campaign).filter((c) => c.id.startsWith("ddb-"));
}

/** The characters that can have a token on the map. NOT the same set as the
 *  ones with a D&D Beyond sheet: a custom character — an NPC ally the DM runs
 *  from a card, a hireling, a summon given a card — has a token and no sheet.
 *  Everything that writes to ROLL20 (bars, tooltip, auras, light, vision) uses
 *  this; only what writes to D&D BEYOND uses campaignSheets. Conflating the
 *  two is why a light set on a custom character's row never reached the map. */
export function campaignTokenFolk(layout: Layout, campaign = layout.activeCampaign): Character[] {
  return campaignRoster(layout, campaign).filter((c) => c.id.startsWith("ddb-") || c.id.startsWith("custom-"));
}

/* ---- One Roll20 game, several tables -------------------------------------------------------------
 *
 * Every campaign of the Korentil game is linked to the SAME Roll20 game (game 17097329), and so is
 * the Test Campaign. Roll20 keeps ONE turn order per game, so whichever table is fighting, every
 * campaign sees that fight in Roll20's tracker.
 *
 * The tracker follows Roll20's turn automatically, and a row it does not recognise is absorbed as
 * "a creature or NPC" without a word (that is what lets the DM drop a new token in mid-fight). With
 * one shared turn order that is a leak: on 2026-10-07 the DM switched to the Test Campaign while the
 * Vaelis fight was still in Roll20, and every Vaelis player and monster poured into the Test
 * Campaign's tracker, the players as if they were monsters. The campaign switch itself was fine: it
 * parked and unpacked the combat exactly as designed. The refill came from Roll20 a moment later.
 *
 * Roll20 rows do not say which campaign they belong to, but the PLAYERS in them do: each is claimed
 * by a roster. So a page that names another table's players and none of this table's is another
 * table's fight, and is not absorbed. */

export interface Roll20Row { name?: string; tokenId?: string }
export interface Roll20Fence {
  /** Rows naming a player of THIS campaign. */
  own: number;
  /** Rows naming a player some OTHER campaign claims and this one does not. */
  foreign: { name: string; campaign: string }[];
  /** Whole page is another table's fight: it names foreign players and none of ours. */
  looksForeign: boolean;
}

/** The players other campaigns claim and the open one does not, by normalised name. */
export function foreignPlayers(layout: Layout, campaign = layout.activeCampaign): Map<string, { name: string; campaign: string }> {
  const mine = rosterIdsOf(layout, campaign);
  const found = new Map<string, { name: string; campaign: string }>();
  for (const snap of layout.campaignSnapshots) {
    if (snap.name === campaign) continue;
    const theirs = rosterIdsOf(layout, snap.name);
    for (const c of layout.characters) {
      if (!theirs.has(c.id) || mine.has(c.id)) continue;
      const key = normLoose(c.name);
      if (key && !found.has(key)) found.set(key, { name: c.name, campaign: snap.name });
    }
  }
  return found;
}

/** Sort a Roll20 turn-order page into ours / theirs. `aliases` is whisperNames (character id to the
 *  name Roll20 knows them by), so a player whose token has another name still counts as ours. */
export function rollPageFence(layout: Layout, rows: Roll20Row[], aliases: Record<string, string> = {}, campaign = layout.activeCampaign): Roll20Fence {
  const roster = campaignRoster(layout, campaign);
  const ownNames = new Set<string>();
  for (const c of roster) { ownNames.add(normLoose(c.name)); if (aliases[c.id]) ownNames.add(normLoose(aliases[c.id])); }
  const ownFirst = new Set(roster.map((c) => normLoose(c.name).split(" ")[0]).filter(Boolean));
  const theirs = foreignPlayers(layout, campaign);
  let own = 0;
  const foreign: Roll20Fence["foreign"] = [];
  for (const row of rows) {
    const key = normLoose(row?.name || "");
    if (!key) continue;
    // Ours is matched as generously as the tracker matches it (exact, alias, first word)...
    if (ownNames.has(key) || ownFirst.has(key.split(" ")[0])) { own++; continue; }
    // ...theirs only on an exact name, so a monster that shares a first word with someone is never swept up.
    const hit = theirs.get(key);
    if (hit) foreign.push(hit);
  }
  return { own, foreign, looksForeign: foreign.length > 0 && own === 0 };
}

/** The names in a fence, for a message the DM can read at a glance. */
export function describeFence(f: Roll20Fence, limit = 4): string {
  const names = [...new Set(f.foreign.map((x) => x.name))];
  const tables = [...new Set(f.foreign.map((x) => x.campaign))];
  const shown = names.slice(0, limit).join(", ") + (names.length > limit ? ` and ${names.length - limit} more` : "");
  return `${shown}${tables.length === 1 ? ` (${tables[0]})` : ""}`;
}
